import {
  BackstageUserIdentity,
  identityApiRef,
  useApi,
} from '@mainstage/core-plugin-api';
import { catalogApiRef } from '@mainstage/plugin-catalog-react';
import { stringifyEntityRef } from '@mainstage/catalog-model';
import useAsync from 'react-use/lib/useAsync';

export interface EnrichedBackstageIdentity extends BackstageUserIdentity {
  ownershipEntityRefs: string[];
}

/**
 * Hook that fetches the user's BackstageIdentity and enriches it with ownershipEntityRefs
 * by fetching them from the catalog API.
 *
 * This is useful when ownershipEntityRefs is not stored in the session cookie
 * due to size limitations (too many groups).
 */
export const useEnrichedIdentity = () => {
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);

  const { value, loading, error } = useAsync(async () => {
    const identity = await identityApi.getBackstageIdentity();

    // If ownershipEntityRefs is already present, return as is
    if (
      identity.ownershipEntityRefs &&
      identity.ownershipEntityRefs.length > 0
    ) {
      return identity as EnrichedBackstageIdentity;
    }

    // Otherwise, fetch from catalog
    try {
      const userEntity = await catalogApi.getEntityByRef(
        identity.userEntityRef,
      );

      if (!userEntity) {
        return {
          ...identity,
          ownershipEntityRefs: [identity.userEntityRef],
        } as EnrichedBackstageIdentity;
      }

      const ownershipEntityRefs: string[] = [identity.userEntityRef];

      if (userEntity.spec?.memberOf) {
        const memberOf = Array.isArray(userEntity.spec.memberOf)
          ? userEntity.spec.memberOf
          : [userEntity.spec.memberOf];

        for (const group of memberOf) {
          try {
            // Parse and normalize the group reference
            const groupRef =
              typeof group === 'string'
                ? group
                : stringifyEntityRef(group as any);
            ownershipEntityRefs.push(groupRef);
          } catch {
            // Skip invalid references
          }
        }
      }

      return {
        ...identity,
        ownershipEntityRefs,
      } as EnrichedBackstageIdentity;
    } catch (catalogError) {
      return {
        ...identity,
        ownershipEntityRefs: [identity.userEntityRef],
      } as EnrichedBackstageIdentity;
    }
  }, [identityApi, catalogApi]);

  return {
    identity: value,
    loading,
    error,
  };
};
